// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CSSVariablesResolver } from '@mantine/core';
import { createTheme, InputWrapper, MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/core/styles.layer.css';
import 'mantine-datatable/styles.layer.css';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import { parsePolarisEnv } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { App } from './App';
import { getProviderMedplumOptions } from './auth-config';
import { ProfileCompletionProvider } from './hooks/useProfileCompletion';

// Fail fast at boot if the Polaris environment is missing or malformed.
// See docs/ENVIRONMENT_ISOLATION_PLAN.md.
const polarisEnv = parsePolarisEnv(import.meta.env.VITE_POLARIS_ENV);

// Seed globalThis with the same env + tenant org id that deploy-bots.ts bakes
// into every bot's compiled source. Client-side modules under `lib/` (e.g.
// updatePatientSafely → getPolarisEnv) read from globalThis so they stay
// runtime-agnostic — bots, tests, scripts, and the browser bundle all share
// one resolution path.
const polarisGlobals = globalThis as { POLARIS_ENV?: string; POLARIS_TENANT_ORG_ID?: string };
polarisGlobals.POLARIS_ENV = polarisEnv;
if (import.meta.env.VITE_MEDPLUM_ORGANIZATION_ID) {
  polarisGlobals.POLARIS_TENANT_ORG_ID = import.meta.env.VITE_MEDPLUM_ORGANIZATION_ID;
}

if (import.meta.env.VITE_PROVIDER_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_PROVIDER_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_PROVIDER_APP_SENTRY_ENVIRONMENT || 'development',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const medplum = new MedplumClient({
  onUnauthenticated: () => (window.location.href = '/'),
  ...getProviderMedplumOptions(),
  // baseUrl: 'http://localhost:8103/', // Uncomment this to run against the server on your localhost
  cacheTime: 60000,
  autoBatchTime: 100,
});

const theme = createTheme({
  components: {
    InputWrapper: InputWrapper.extend({
      styles: {
        label: { fontWeight: 600 },
      },
    }),
  },
  primaryColor: 'polaris',
  colors: {
    // Generated from #640040 — shade 5 is the primary brand color.
    // Mantine uses shades 0-4 for light variants/backgrounds and 6-9 for hover/active/dark states.
    polaris: [
      '#fef0f5', // 0: lightest (backgrounds)
      '#f5d0de', // 1: light (unselected pills, hover bg)
      '#e8a1bc', // 2
      '#d6729a', // 3
      '#c44a7b', // 4
      '#640040', // 5: primary (buttons, active states)
      '#560037', // 6: hover
      '#47002e', // 7: active/pressed
      '#380024', // 8
      '#2a001b', // 9: darkest
    ],
  },
  headings: {
    sizes: {
      h1: {
        fontSize: '1.125rem',
        fontWeight: '500',
        lineHeight: '2.0',
      },
    },
  },
  fontSizes: {
    xs: '0.6875rem',
    sm: '0.875rem',
    md: '0.875rem',
    lg: '1.0rem',
    xl: '1.125rem',
  },
});

const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--polaris-light-purple': '#FEF4FF',
  },
  light: {
    '--mantine-color-body': '#ffffff',
    '--mantine-primary-color-light-hover': 'transparent',
  },
  dark: {},
});

const router = createBrowserRouter([{ path: '*', element: <App /> }]);

const navigate = (path: string): Promise<void> => router.navigate(path);

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
root.render(
  <StrictMode>
    <MedplumProvider medplum={medplum} navigate={navigate}>
      <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
        <Notifications position="bottom-right" />
        <ProfileCompletionProvider>
          <RouterProvider router={router} />
        </ProfileCompletionProvider>
      </MantineProvider>
    </MedplumProvider>
  </StrictMode>
);
